import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import axios from "axios";
import NormalTable from "../../../components/common/Tables/NormalTable";
import { createOrganizerCoupon, getAllTicketBySlug, updateOrganizerCoupon, deleteCoupon, getAllCoupon, checkPremiumEntitlementAccess, getAllEvents, getEventById } from "../../../api";
import { PulseLoader } from "react-spinners";
import { toast } from "react-toastify";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import FormattedDate from "../../../components/common/DateFormat/FormattedDate";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  FormGroup,
  FormFeedback,
  Label,
  Form,
} from "reactstrap";
import { Cell } from "jspdf-autotable";
import ListCard from "../../../components/common/ListCard/ListCard";
import Select from "react-select";
import { colourStyles } from "../../../utils/customSelectStyle";
import { useAuth } from "../../../context/AuthContext";
import { useNightMode } from "../../../contexts/NightModeContext";
import Background from "../../Login/Background";
import PageHeader from "../../../components/common/PageHeader/PageHeader";
import { Search } from "lucide-react";
import ConfirmationDialog from "../../../components/common/ConfirmationDialog/ConfirmationDialog";

interface Coupon {
  Event: any;
  couponId: number;
  code: string;
  discount: number;
  discountType: string;
  maxCount: number;
  totalUsed: number;
  eventName: string;
  ticketType: string;
  createdAt: string;
  status: string;
  TicketType?: {
    ticketType: string;
    ticketGroupId: string;
    groupName: string;
    groupingId: string;
    Venue?: {
      venueName: string;
    };
  };
}

interface PremiumEntitlementResponse {
  success: boolean;
  hasAccess: boolean;
  message: string;
  error?: string;
  data?: {
    organizerId: number;
    entitlementKey: string;
    entitlementName: string;
    isPremiumEntitlement: boolean;
    isPremiumUser: boolean;
  };
}

const Coupons: React.FC = () => {
  const { user } = useAuth();
  const { isNightMode } = useNightMode();
  const { slug, eventId } = useParams();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [search, setSearch] = useState<string>("");
  const [modal, setModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [ticketList, setTicketList] = useState<any[]>([]);
  const toggle = () => {
    if (!modal) {
      // Opening modal: reset form and pre-populate if single group
      validation.resetForm();
      if (singleGroup) {
        validation.setFieldValue("groupingId", String(singleGroup.value));
        validation.setFieldValue("groupName", singleGroup.label);
      }
    }
    setModal(!modal);
  };
  const [editModal, setEditModal] = useState<boolean>(false);
  const [editCoupon, setEditCoupon] = useState<Coupon>({} as Coupon);
  const [groupTicketList, setGroupTicketList] = useState<any[]>([]);
  const [ticketGroups, setTicketGroups] = useState<any[]>([]);
  const [ticketOptions, setTicketOptions] = useState<any[]>([]);
  const toggleEditModal = () => setEditModal(!editModal);
  const [hideGroupSelect, setHideGroupSelect] = useState(false);
  const [selectedTicketFilter, setSelectedTicketFilter] = useState<string>("all");
  const [singleGroup, setSingleGroup] = useState<any>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; coupon: any | null }>({ isOpen: false, coupon: null });
  const ticketMultiStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      border: state.isFocused ? "1px solid #EE3F7C" : "1px solid rgba(255, 255, 255, 0.07)",
      borderRadius: "12px",
      minHeight: "46px",
      height: "auto",
      boxShadow: "none",
      color: "#ffffff",
      "&:hover": {
        borderColor: "rgba(238, 63, 124, 0.55)",
      },
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: "4px 12px",
      flexWrap: "wrap",
    }),
    placeholder: (base: any) => ({
      ...base,
      color: "rgba(255, 255, 255, 0.45)",
      fontSize: "14px",
      margin: 0,
    }),
    singleValue: (base: any) => ({
      ...base,
      color: "#ffffff",
      margin: 0,
    }),
    input: (base: any) => ({
      ...base,
      color: "#ffffff",
      margin: 0,
      padding: 0,
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: "rgba(238, 63, 124, 0.25)",
      border: "1px solid rgba(238, 63, 124, 0.4)",
      borderRadius: "8px",
      margin: "2px",
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      color: "#ffffff",
      fontSize: "12px",
      fontWeight: 600,
      padding: "2px 6px",
    }),
    multiValueRemove: (base: any) => ({
      ...base,
      color: "rgba(255, 255, 255, 0.7)",
      ":hover": {
        backgroundColor: "rgba(238, 63, 124, 0.5)",
        color: "#ffffff",
      },
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: "rgba(255, 255, 255, 0.45)",
      ":hover": {
        color: "#ffffff",
      },
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: "#181018",
      border: "1px solid rgba(255, 255, 255, 0.12)",
      borderRadius: "14px",
      zIndex: 99999,
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "rgba(238, 63, 124, 0.3)"
        : state.isFocused
        ? "rgba(255, 255, 255, 0.08)"
        : "transparent",
      color: "#ffffff",
      cursor: "pointer",
      fontSize: "13px",
    }),
  };

  useEffect(() => {
    getTicketList();
  }, [slug, eventId]);

  const getCouponsList = async () => {
    try {
      setIsLoading(true);
      const response = await getAllCoupon();
      const eventData = localStorage.getItem("eventData");
      const parsed = eventData ? JSON.parse(eventData) : null;
      const targetId = parsed?.eventId || eventId || slug;

      if (Array.isArray(response)) {
        const filteredCoupons = response.filter(
          (coupon: any) =>
            !targetId ||
            String(coupon.Event?.eventId) === String(targetId) ||
            String(coupon.eventId) === String(targetId)
        );
        setCoupons(filteredCoupons);
      } else {
        setCoupons([]);
      }
    } catch (error) {
      console.error("Error fetching coupons:", error);
      setCoupons([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getResolvedSlug = async (targetId?: string): Promise<string> => {
    const idToFind = targetId || eventId || slug;
    const eventData = localStorage.getItem("eventData");
    if (eventData) {
      try {
        const parsed = JSON.parse(eventData);
        if (
          parsed.slugId &&
          isNaN(Number(parsed.slugId)) &&
          (!idToFind || String(parsed.eventId) === String(idToFind) || String(parsed.slugId) === String(idToFind))
        ) {
          return parsed.slugId;
        }
      } catch (e) {}
    }
    if (idToFind && isNaN(Number(idToFind))) {
      return idToFind;
    }
    if (idToFind) {
      try {
        const eventDetails = await getEventById(idToFind);
        if (eventDetails && eventDetails.slugId) {
          localStorage.setItem("eventData", JSON.stringify(eventDetails));
          return eventDetails.slugId;
        }
      } catch (err) {
        console.error("Error fetching event by ID:", err);
      }
    }
    try {
      const res: any = await getAllEvents();
      const list = Array.isArray(res) ? res : res?.data || [];
      const found = list.find(
        (e: any) => String(e.eventId) === String(idToFind) || String(e.id) === String(idToFind)
      );
      if (found) {
        localStorage.setItem("eventData", JSON.stringify(found));
        if (found.slugId) return found.slugId;
      }
    } catch (err) {
      console.error("Error resolving slugId:", err);
    }
    return idToFind || "";
  };

  const getTicketList = async () => {
    try {
      setIsLoading(true);
      const eventSlug = await getResolvedSlug();

      if (eventSlug) {
        const response = await getAllTicketBySlug(eventSlug);
        const ticketArr = Array.isArray(response) ? response : [];

        const processedTickets = ticketArr.map((t: any) => ({
          ...t,
          groupingId: t.groupingId ?? null,
          groupName: t.groupName ?? null,
          ticketTypeId: t.ticketTypeId ?? t.id,
          ticketType: t.ticketType ?? "Unnamed Ticket",
        }));

        setTicketList(processedTickets);

        // Create ticket options for React Select
        const ticketSelectOptions = ticketArr.map((ticket: any) => ({
          value: ticket.ticketTypeId,
          label: `${ticket.ticketType}${
            ticket["Venue.venueName"]
              ? ` (${ticket["Venue.venueName"]})`
              : ""
          }`,
          groupingId: ticket.groupingId,
        }));
        setTicketOptions(ticketSelectOptions);

        const seen: Record<string, boolean> = {};
        const groupOptions = ticketArr
          .filter((ticket: any) => {
            if (!ticket.groupingId) return false;
            if (!ticket.groupName) return false;
            if (seen[ticket.groupingId]) return false;
            seen[ticket.groupingId] = true;
            return true;
          })
          .map((ticket: any) => ({
            value: ticket.groupingId,
            label: ticket.groupName,
          }));
        setGroupTicketList(groupOptions);
        setTicketGroups(groupOptions);
        if (groupOptions.length === 1) {
          setSingleGroup(groupOptions[0]);
          setHideGroupSelect(true);
        } else {
          setSingleGroup(null);
          setHideGroupSelect(false);
        }
      }
    } catch (error: any) {
      console.error("Error fetching tickets for coupons:", error);
    } finally {
      getCouponsList();
    }
  };


  interface CouponFormValues {
    code: string;
    discount: string;
    discountType: string;
    status: string;
    maxCount: string;
    groupingId: string;
    groupName: string;
    ticketType: string[];
  }
  const validation = useFormik<CouponFormValues>({
    initialValues: {
      code: "",
      discount: "",
      discountType: "",
      status: "active",
      ticketType: [],
      maxCount: "",
      groupingId: "",
      groupName: "",
    },
    validationSchema: Yup.object({
      code: Yup.string().required("Coupon Code is required"),
      discount: Yup.number().typeError("Discount must be a number").required("Discount is required"),
      discountType: Yup.string().required("Discount Type is required"),
      maxCount: Yup.number().typeError("Max Count must be a number").required("Max Count is required"),
      ticketType: Yup.array().min(1, "Please select at least one ticket"),
      groupingId: Yup.string().optional(),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        setIsLoading(true);
        const eventData = localStorage.getItem("eventData");
        if (eventData) {
          const eventId = JSON.parse(eventData).eventId;
          const requests = values.ticketType.map(ticketId => {
            const payload = {
              ...values,
              eventId,
              ticketTypeId: ticketId,
              status: "active",
             
            };
            return createOrganizerCoupon(payload);
          });

          await Promise.all(requests); 
          toast.success("Coupons created successfully!");
          resetForm();
          toggle();
          getCouponsList();
        }
      } catch (error) {
        console.error("Error creating Coupon:", error);
        toast.error("Error creating Coupon. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
  });
  const updateCoupon = async () => {
    try {
      setIsLoading(true);
      const payload = {
        ...editCoupon,
        status: editCoupon.status,
      };
      const response = await updateOrganizerCoupon(editCoupon.couponId, payload);
      toast.success("Coupon updated successfully!");
      toggleEditModal();
      getCouponsList();
    } catch (error) {
      console.error("Error updating Coupon:", error);
    }
    finally {
      setIsLoading(false);
    }
  };
  const handleDelete = (row: any) => {
    setDeleteConfirm({ isOpen: true, coupon: row });
  };
  const confirmDelete = async () => {
    try {
      await deleteCoupon(deleteConfirm.coupon.couponId);
      toast.success("Coupon deleted successfully!");
      getCouponsList();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || "Failed to delete coupon.";
      toast.error(errorMsg);
    }
  };

  const columns = [ 
    { label: "Coupon Code", key: "code" },
    { label: "Discount", key: "discount" },
    { label: "Discount Type", key: "discountType" },
    { label: "Max Count", key: "maxCount" },
    { label: "Total Used", key: "totalUsed" },
    {
      label: "Ticket",
      key: "ticketType",
      cellContent: (row: any) => <span>{row?.TicketType?.ticketType + " (" + row?.TicketType?.Venue?.venueName + ")"}</span>,
    },
    { label: "Created At", key: "createdAt" ,
      cellContent: (row: any) => <FormattedDate dateString={row?.createdAt} />
    },
    { label: "Status", key: "status", cellContent: (row: any) => <span className={row?.status === "active" ? "text-success" : "text-danger"}>{row?.status?.charAt(0).toUpperCase() + row?.status?.slice(1)}</span> },
    {
      label: "",
      key: "edit",
      cellContent: (row: any) => (
        <span className="d-flex justify-content-center align-items-center gap-4 cursor-pointer">
        <span className="text-primary"
          onClick={() => handleEdit(row)} >
          <i className="fa-solid fa-pencil"></i>
        </span>
         <span className="text-danger"
          onClick={() => handleDelete(row)} >
          <i className="fa-solid fa-trash"></i>
        </span>
        </span>
      ),
    },
  ];

  const handleEditChange = (e: any) => {
    setEditCoupon({ ...editCoupon, [e.target.name]: e.target.value });
  };
  const handleEdit = (row: any) => {
    setEditCoupon(row);
    toggleEditModal();
  };
  const handleSearch = (e: any) => {
    const value = e.target.value;
    setSearch(value);
    if (value.length > 0) {
      const filtered = coupons.filter((coupon) =>
        (coupon.code ?? "").toLowerCase().startsWith(value.toLowerCase())
      );
      setCoupons(filtered);
    } else {
      getCouponsList();
    }
  };

  const getTicketFromGroup = (groupingId: string) => {
    const ticketGroup = ticketGroups.find((ticket: any) => parseInt(ticket.value) === parseInt(groupingId));
    return ticketGroup?.label || "N/A";
  };
  return (
    <div className={`contact-list-container ${isNightMode ? 'text-white' : 'text-dark'}`}>
      <Background />
      <PageHeader
        title="Coupon List"
        subtitle="Manage organizer promotional coupons, track usage limits, and create new discounts for event tickets."
        isLoading={isLoading}
        actions={
          <Button color="danger" onClick={toggle} className="v3-btn-primary h_40 d-flex align-items-center">
            <i className="fa-solid fa-plus me-2"></i> Add New Coupon
          </Button>
        }
      />

      <div className="contact-filter-card mb-3 mt-4">
        <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
          <div className="position-relative flex-grow-1 w-100">
            <Search className="position-absolute start-0 top-50 translate-middle-y ms-3 contact-sub-text" size={18} />
            <input
              type="text"
              className="contact-search-input"
              placeholder="Search by Coupon Code..."
              value={search}
              onChange={handleSearch}
            />
          </div>
        </div>
      </div>

      {ticketList.length > 0 && (
        <div className="d-flex align-items-center gap-2 flex-wrap mb-4 ms-1">
          <span className="text-muted fs-12 fw-semibold me-1">
            <i className="fa-solid fa-filter me-1"></i>Filter by Ticket:
          </span>
          <button
            type="button"
            className={`btn btn-sm ${selectedTicketFilter === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
            style={{ borderRadius: '20px', fontSize: '11px', padding: '5px 14px', fontWeight: 600 }}
            onClick={() => setSelectedTicketFilter('all')}
          >
            All Tickets ({coupons.length})
          </button>
          {ticketList.map((ticket: any) => {
            const tId = String(ticket.ticketTypeId || ticket.id);
            const count = coupons.filter((c: any) => 
              String(c.TicketType?.ticketTypeId || c.ticketTypeId || c.TicketType?.id || "") === tId
            ).length;
            return (
              <button
                key={tId}
                type="button"
                className={`btn btn-sm ${selectedTicketFilter === tId ? 'btn-primary' : 'btn-outline-secondary'}`}
                style={{ borderRadius: '20px', fontSize: '11px', padding: '5px 14px', fontWeight: 600 }}
                onClick={() => setSelectedTicketFilter(tId)}
              >
                {ticket.ticketType || ticket.name} ({count})
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-2">
        {isLoading && (
          <div className="d-flex justify-content-center py-4">
            <PulseLoader color="#3470ca" size={9} speedMultiplier={1} />
          </div>
        )}
        {!isLoading && coupons.length > 0 ? (
          coupons
            .filter((coupon: any) => {
              if (selectedTicketFilter === 'all') return true;
              const couponTicketId = String(coupon.TicketType?.ticketTypeId || coupon.ticketTypeId || coupon.TicketType?.id || "");
              return couponTicketId === selectedTicketFilter;
            })
            .map((coupon) => (
              <ListCard
                key={coupon.couponId}
                cardData={{
                  icon: "fa-solid fa-ticket",
                  name: coupon.code,
                  ticket: coupon.TicketType?.ticketType || "N/A",
                  link: "https://eventox.co/event/" + coupon.Event?.slugId + "/?couponcode=" + coupon.code,
                  startDate: coupon.createdAt,
                  status: coupon.status,
                  totalUsed: coupon.totalUsed,
                  totalLimit: coupon.maxCount,
                  discount: coupon.discount + (coupon.discountType === "percentage" ? "%" : ""),
                  isActive: coupon.status === "active",
                  optionHead: "Discount",
                  option: coupon.discount + (coupon.discountType === "percentage" ? "%" : ""),
                  ticketGroup: getTicketFromGroup(coupon.TicketType?.groupingId || "") || "N/A",
                  optionIcon1: "fa-solid fa-users",
                }}
                onEdit={() => handleEdit(coupon)}
                onDelete={() => handleDelete(coupon)}
                onToggle={(val) => {
                  setEditCoupon({ ...coupon, status: val ? "active" : "inactive" });
                  updateCoupon();
                }}
              />
            ))
        ) : (
          !isLoading && (
            <p className="text-muted">No coupons available</p>
          )
        )}
      </div>
      <div>
        <Modal isOpen={modal} toggle={toggle} modalClassName="quick-v2-glass-modal" size="lg">
          <ModalHeader toggle={toggle} className="border-bottom border-secondary">
            <span style={{ fontSize: '1.1rem', fontWeight: 600, letterSpacing: '0.5px' }}>
              🏷️ Add New Coupon
            </span>
          </ModalHeader>
          <ModalBody className="p-4">
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
              }}
            >
              <FormGroup>
                <Label className="text-uppercase text-secondary font-monospace" style={{ fontSize: '11px', letterSpacing: '1px' }}>
                  COUPON CODE *
                </Label>
                <Input
                  name="code"
                  type="text"
                  placeholder="e.g. SUMMER50"
                  className="bg-dark text-white border-secondary"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.code}
                  invalid={
                    !!(
                      validation.touched.code &&
                      validation.errors.code
                    )
                  }
                />
                <FormFeedback>{validation.errors.code}</FormFeedback>
              </FormGroup>
             {!hideGroupSelect && (
                <FormGroup>
                  <Label className="text-uppercase text-secondary font-monospace" style={{ fontSize: '11px', letterSpacing: '1px' }}>
                    TICKET GROUP
                  </Label>
                  <Input
                    type="select"
                    name="groupingId"
                    className="bg-dark text-white border-secondary"
                    onChange={(e) => {
                      validation.handleChange(e);
                      // Reset ticket selection when group changes
                      validation.setFieldValue("ticketType", []);
                    }}
                    onBlur={validation.handleBlur}
                    value={validation.values.groupingId}
                    invalid={!!(validation.touched.groupingId && validation.errors.groupingId)}
                  >
                    <option value="">Select Ticket Group</option>
                    {ticketGroups.map((group: any) => (
                      <option key={group.value} value={group.value}>
                        {group.label}
                      </option>
                    ))}
                  </Input>
                  <FormFeedback>{validation.errors.groupingId}</FormFeedback>
                </FormGroup>
              )}
              <FormGroup>
                <Label className="text-uppercase text-secondary font-monospace" style={{ fontSize: '11px', letterSpacing: '1px' }}>
                  TICKETS
                </Label>
                <Select
                  isMulti
                  name="ticketType"
                    
                  options={ticketOptions.filter(
                    (option: any) =>
                      !validation.values.groupingId ||
                      String(option.groupingId) === String(validation.values.groupingId) ||
                      !option.groupingId
                  )}
                  value={ticketOptions.filter((option: any) =>
                    validation.values.ticketType?.includes(option.value)
                  )}
                  onChange={(selectedOptions: any) => {
                    const selectedValues = selectedOptions
                      ? selectedOptions.map((option: any) => option.value)
                      : [];
                    validation.setFieldValue("ticketType", selectedValues);
                  }}
                  onBlur={() => validation.setFieldTouched("ticketType", true)}
                  placeholder="Select tickets..."
                  styles={ticketMultiStyles}
                  className="react-select-container eventox-select"
                  classNamePrefix="react-select"
                  isClearable
                  isSearchable
                />
                {validation.touched.ticketType && validation.errors.ticketType && (
                  <div className="invalid-feedback d-block">
                    {validation.errors.ticketType}
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label className="text-uppercase text-secondary font-monospace" style={{ fontSize: '11px', letterSpacing: '1px' }}>
                  DISCOUNT TYPE *
                </Label>
                <Input
                  type="select"
                  name="discountType"
                  className="bg-dark text-white border-secondary"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.discountType}
                  invalid={
                    !!(
                      validation.touched.discountType &&
                      validation.errors.discountType
                    )
                  }
                >
                  <option value="">Select Discount Type</option>
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed</option> 
                </Input>
                <FormFeedback>{validation.errors.discountType}</FormFeedback>
              </FormGroup>

              <FormGroup>
                <Label className="text-uppercase text-secondary font-monospace" style={{ fontSize: '11px', letterSpacing: '1px' }}>
                  DISCOUNT *
                </Label>
                <Input
                  name="discount"
                  type="text"
                  placeholder="Enter Discount"
                  className="bg-dark text-white border-secondary"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.discount}
                  invalid={
                    !!(
                      validation.touched.discount && validation.errors.discount
                    )
                  }
                />
                <FormFeedback>{validation.errors.discount}</FormFeedback>
              </FormGroup>

              <FormGroup>
                <Label className="text-uppercase text-secondary font-monospace" style={{ fontSize: '11px', letterSpacing: '1px' }}>
                  MAX COUNT *
                </Label>
                <Input
                  name="maxCount"
                  type="number"
                  placeholder="e.g. 100"
                  className="bg-dark text-white border-secondary"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.maxCount}
                  invalid={
                    !!(
                      validation.touched.maxCount && validation.errors.maxCount
                    )
                  }
                />
                <FormFeedback>{validation.errors.maxCount}</FormFeedback>
              </FormGroup>
              <ModalFooter className="border-0 pt-0">
                <Button type="button" outline color="light" onClick={toggle} className="me-2 rounded-pill px-4">
                  Cancel
                </Button>
                <Button color="primary" disabled={isLoading} type="submit" className="btn-primary rounded-pill px-4">
                  <i className="fa-solid fa-floppy-disk me-2"></i>
                  {isLoading ? "Saving..." : "Save Coupon"}
                </Button>
              </ModalFooter>
            </Form>
          </ModalBody>
        </Modal>
        <Modal isOpen={editModal} toggle={toggleEditModal} modalClassName="quick-v2-glass-modal" size="lg">
          <ModalHeader toggle={toggleEditModal} className="border-bottom border-secondary">
            <span style={{ fontSize: '1.1rem', fontWeight: 600, letterSpacing: '0.5px' }}>
              🏷️ Edit Coupon
            </span>
          </ModalHeader>
          <ModalBody className="p-4">
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                updateCoupon();
              }}
            >
              <FormGroup>
                <Label className="text-uppercase text-secondary font-monospace" style={{ fontSize: '11px', letterSpacing: '1px' }}>
                  COUPON CODE
                </Label>
                <Input type="text" disabled name="code" className="bg-dark text-white border-secondary" value={editCoupon.code} onChange={handleEditChange} />
              </FormGroup>
              <FormGroup>
                <Label className="text-uppercase text-secondary font-monospace" style={{ fontSize: '11px', letterSpacing: '1px' }}>
                  MAX COUNT
                </Label>
                <Input type="text" name="maxCount" className="bg-dark text-white border-secondary" value={editCoupon.maxCount} onChange={handleEditChange} />
              </FormGroup>
              <FormGroup>
                <Label className="text-uppercase text-secondary font-monospace" style={{ fontSize: '11px', letterSpacing: '1px' }}>
                  STATUS
                </Label>
                <Input
                  type="select"
                  name="status"
                  className="bg-dark text-white border-secondary"
                  onChange={handleEditChange}
                  value={editCoupon.status}
                >
                  <option value="">Select Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Input>
              </FormGroup>
              
              <ModalFooter className="border-0 pt-0">
                <Button type="button" outline color="light" onClick={toggleEditModal} className="me-2 rounded-pill px-4">
                  Cancel
                </Button>
                <Button color="primary" disabled={isLoading} type="submit" className="btn-primary rounded-pill px-4">
                  <i className="fa-solid fa-floppy-disk me-2"></i>
                  {isLoading ? "Saving..." : "Save Coupon"}
                </Button>
              </ModalFooter>
            </Form>
          </ModalBody>
        </Modal>
      </div>

      <ConfirmationDialog
        isOpen={deleteConfirm.isOpen}
        toggle={() => setDeleteConfirm({ isOpen: false, coupon: null })}
        onConfirm={confirmDelete}
        title="Delete Coupon"
        message={`Are you sure you want to permanently delete coupon <strong>${deleteConfirm.coupon?.code || 'this coupon'}</strong>? This action cannot be undone.`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
        icon="fa-solid fa-trash"
      />
      <div>
        <ToastContainer
        autoClose={2000}
        />
      </div>
    </div>
  );
};
export default Coupons;